import { v4 as uuidv4 } from 'uuid';

/**
 * Native Bridge for Flutter ↔ Web communication
 * 
 * This module provides a unified interface for communicating with the native
 * Flutter wrapper when running as a hybrid app, with fallbacks for web-only mode.
 */

export interface BridgeMessage {
  id: string;
  type: 'request' | 'response' | 'event';
  channel: string;
  action: string;
  payload?: any;
  error?: string;
}

type MessageHandler = (payload: any) => Promise<any> | any;

declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (name: string, ...args: any[]) => Promise<any>;
    };
    OutCloseBridge?: {
      isNative: boolean;
      platform: string;
      version?: string;
    };
  }
}

class NativeBridge {
  private handlers: Map<string, MessageHandler> = new Map();
  private pendingRequests: Map<string, { 
    resolve: (value: any) => void; 
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
  }> = new Map();
  
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  constructor() {
    // Listen for messages from Flutter via postMessage
    window.addEventListener('message', this.handleMessage.bind(this));
    
    console.log('[Bridge] Initialized, isNative:', this.isNative);
  }

  /** Check if running inside native app */
  get isNative(): boolean {
    return !!(window.flutter_inappwebview || window.OutCloseBridge?.isNative);
  }

  /** Get the native platform (flutter, etc.) */
  get platform(): string | undefined {
    return window.OutCloseBridge?.platform;
  }

  /** Send request to native and await response */
  async request<T = any>(channel: string, action: string, payload?: any, timeoutMs: number = this.REQUEST_TIMEOUT): Promise<T> {
    if (!this.isNative) {
      throw new Error('Not running in native context');
    }

    const id = uuidv4();
    const message: BridgeMessage = { 
      id, 
      type: 'request', 
      channel, 
      action, 
      payload 
    };

    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Bridge request timeout: ${channel}:${action}`));
      }, timeoutMs);

      this.pendingRequests.set(id, { resolve, reject, timeout });

      // Send to Flutter
      this.sendToNative(message);
    });
  }

  /** Register handler for native events/requests */
  on(channel: string, action: string, handler: MessageHandler): () => void {
    const key = `${channel}:${action}`;
    this.handlers.set(key, handler);
    
    // Return unsubscribe function
    return () => {
      this.handlers.delete(key);
    };
  }

  /** Remove handler */
  off(channel: string, action: string): void {
    const key = `${channel}:${action}`;
    this.handlers.delete(key);
  }

  /** Send event to native (fire and forget) */
  emit(channel: string, action: string, payload?: any): void {
    if (!this.isNative) return;

    const message: BridgeMessage = {
      id: uuidv4(),
      type: 'event',
      channel,
      action,
      payload,
    };

    this.sendToNative(message);
  }

  private sendToNative(message: BridgeMessage): void {
    try {
      const messageJson = JSON.stringify(message);
      window.flutter_inappwebview?.callHandler('bridgeMessage', messageJson);
    } catch (e) {
      console.error('[Bridge] Error sending to native:', e);
    }
  }

  private handleMessage(event: MessageEvent): void {
    // Only process string messages that look like our protocol
    if (typeof event.data !== 'string') return;
    
    try {
      const message: BridgeMessage = JSON.parse(event.data);
      
      // Validate it's our message format
      if (!message.id || !message.type) return;
      
      this.processMessage(message);
    } catch {
      // Not a JSON message or not our format, ignore
    }
  }

  private async processMessage(message: BridgeMessage): Promise<void> {
    if (message.type === 'response') {
      // Handle response to our request
      const pending = this.pendingRequests.get(message.id);
      if (pending) {
        clearTimeout(pending.timeout);
        this.pendingRequests.delete(message.id);
        
        if (message.error) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.payload);
        }
      }
    } else if (message.type === 'event' || message.type === 'request') {
      // Handle event or request from native
      const handler = this.handlers.get(`${message.channel}:${message.action}`);
      
      if (handler) {
        try {
          const result = await handler(message.payload);
          
          // Send response back if it was a request
          if (message.type === 'request') {
            this.sendResponse(message.id, result);
          }
        } catch (e) {
          if (message.type === 'request') {
            this.sendResponse(message.id, null, String(e));
          }
        }
      }
    }
  }

  private sendResponse(id: string, payload: any, error?: string): void {
    const response: BridgeMessage = { 
      id, 
      type: 'response', 
      channel: '', 
      action: '', 
      payload, 
      error 
    };
    
    try {
      const messageJson = JSON.stringify(response);
      window.flutter_inappwebview?.callHandler('bridgeResponse', messageJson);
    } catch (e) {
      console.error('[Bridge] Error sending response:', e);
    }
  }

  /** Clean up all handlers and pending requests */
  destroy(): void {
    this.handlers.clear();
    this.pendingRequests.forEach(({ timeout }) => clearTimeout(timeout));
    this.pendingRequests.clear();
    window.removeEventListener('message', this.handleMessage.bind(this));
  }
}

// Export singleton instance
export const bridge = new NativeBridge();

/** Register a navigation handler for native-initiated navigation */
export function registerBridgeNavigator(navigate: (path: string) => void): () => void {
  return bridge.on(BridgeChannels.NAVIGATION, BridgeActions.NAVIGATE, ({ path }) => {
    navigate(path);
  });
}

// Export bridge channels and actions for type safety
export const BridgeChannels = {
  AUTH: 'auth',
  PUSH: 'push',
  STORAGE: 'storage',
  NAVIGATION: 'navigation',
  HAPTICS: 'haptics',
  DEVICE: 'device',
  PURCHASE: 'purchase',
} as const;

export const BridgeActions = {
  // Auth
  GET_TOKEN: 'getToken',
  SIGN_OUT: 'signOut',
  STATE_CHANGED: 'stateChanged',
  SET_CREDENTIALS: 'setCredentials',
  SHOW_NATIVE_SIGN_IN: 'showNativeSignIn',
  SHOW_ACCOUNT_DIALOG: 'showAccountDialog',
  
  // Push
  REQUEST_PERMISSION: 'requestPermission',
  GET_PUSH_TOKEN: 'getToken',
  GET_PERMISSION_STATUS: 'getPermissionStatus',
  NOTIFICATION: 'notification',
  
  // Storage
  GET: 'get',
  SET: 'set',
  DELETE: 'delete',
  GET_SECURE: 'getSecure',
  SET_SECURE: 'setSecure',
  DELETE_SECURE: 'deleteSecure',
  
  // Navigation
  OPEN_EXTERNAL: 'openExternal',
  CAN_GO_BACK: 'canGoBack',
  GO_BACK: 'goBack',
  DEEP_LINK: 'deepLink',
  NAVIGATE: 'navigate',
  
  // Haptics
  IMPACT: 'impact',
  SELECTION: 'selection',

  // Device
  THEME_CHANGED: 'themeChanged',
  GET_THEME: 'getTheme',

  // Purchase
  PURCHASE_PRODUCT: 'purchaseProduct',
} as const;

